import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { OrganisationListItem } from "@/app/Organisations/GraphQL/organisationListQuery.graphql";
import { StarIcon } from "@heroicons/react/20/solid";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  deleteOrganisationMutation,
  removeSalesAgentMutation,
  updateOrganisationMutation,
} from "@/app/Organisations/GraphQL/organisationMutations.graphql";
import { useAuth } from "@/lib/Auth/useAuth";
import { useUser } from "@/lib/Auth/useUser";
import { useOrganisationSwitcher } from "@/app/Organisations/Hooks/useOrganisationSwitcher";
import { AssignOrganisationSalesAgentDialog } from "@/app/Organisations/Components/AssignOrganisationSalesAgentDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { DollarSignIcon } from "lucide-react";
import { FlagIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FeatureFlagDialog } from "@/app/Meta/Components/FeatureFlagDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useNavigate } from "@tanstack/react-router";

export function useOrganisationActions(): ActionsHook<OrganisationListItem> {
  const { mutateAsync } = useGqlMutation(updateOrganisationMutation);
  const { open } = useDialog(AssignOrganisationSalesAgentDialog);
  const { mutateAsync: removeSalesAgent } = useGqlMutation(
    removeSalesAgentMutation,
  );
  const { open: openFeatureFlagDialog } = useDialog(FeatureFlagDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deleteOrganisation } = useGqlMutation(
    deleteOrganisationMutation,
  );
  const navigate = useNavigate();

  const { organisations } = useAuth();
  const { switchOrganisation } = useOrganisationSwitcher();
  const user = useUser();

  return (data) => {
    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Verify",
                Icon: StarIcon,
                isHidden: data.is_verified,
                handleAction: async () => {
                  await mutateAsync({
                    id: data.id,
                    input: {
                      is_verified: true,
                    },
                  });

                  if (data.salesAgent) return;

                  open({
                    organisationId: data.id,
                    userId: user.id,
                    subtitle: `${data.name} is now verified. Assign a sales agent.`,
                  });
                },
                color: "indigo",
              },
            ],
          },
          {
            actions: [
              {
                label: "Edit feature flags",
                Icon: FlagIcon,
                handleAction: async () => {
                  openFeatureFlagDialog({ organisationId: data.id });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Assign Sales Agent",
                Icon: DollarSignIcon,
                handleAction: async () => {
                  open({ organisationId: data.id });
                },
              },
              {
                label: "Remove Sales Agent",
                Icon: XMarkIcon,
                isHidden: !data.salesAgent,
                handleAction: async () => {
                  if (!data.salesAgent) return;
                  return removeSalesAgent({
                    orgId: data.id,
                    userId: data.salesAgent.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Make current",
                handleAction: async () => {
                  // Switching scope is a URL navigation now. is_admin isn't on
                  // the org-list row, so cross-ref the user's memberships for
                  // the tenant prefix (defaults to the `/org` tenant view).
                  const membership = organisations.find(
                    (org) => String(org.id) === String(data.id),
                  );
                  await switchOrganisation({
                    id: data.id,
                    is_admin: membership?.is_admin ?? false,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Delete",
                Icon: TrashIcon,
                color: "red",
                isAdminAction: true,
                handleAction: () => {
                  openConfirmDelete({
                    title: "Delete organisation",
                    message: `Are you sure you want to delete ${data.name}? This cannot be undone. Organisations with bookings or invoices cannot be deleted.`,
                    buttonText: "Delete",
                    onDelete: async () => {
                      await deleteOrganisation({ id: data.id });

                      await navigate({
                        from: "/$tenant/$orgId/supplier/{-$supplierId}",
                        to: "/$tenant/$orgId/supplier/{-$supplierId}/organisations",
                        params: (prev) => prev,
                      });
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
